import React, { useState, useEffect } from "react";
import { SearchIcon, Plus, Loader2 } from "lucide-react";
import { useMentorApi } from "@/api/MentorApi";
import { useMentorStore } from "@/stores/mentorStore";
import { Mentor } from "@/stores/mentorStore";
import { Button } from "@/components/ui/button";
import AddMentor from "@/pages/mentorat/AddMentor";

const MentorList: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { getMentorList, submitMentoringRequest } = useMentorApi();
  const { addMentor, openModal, modal, closeModal } = useMentorStore();

  const fetchMentors = async () => {
    setLoading(true);
    try {
      const apiMentors = await getMentorList();
      if (apiMentors && Array.isArray(apiMentors)) {
        const mappedMentors: Mentor[] = apiMentors.map((m) => ({
          id: String(m.id),
          name: `${m.Prenom_mentor} ${m.Nom_mentor}`,
          email: `mentor${m.id}@placeholder.com`,
          profession: [m.Profession],
          dateAdded: new Date().toISOString(),
          lastConnected: m.LastConnected,
          image: `https://randomuser.me/api/portraits/${
            Math.random() > 0.5 ? "men" : "women"
          }/${Math.floor(Math.random() * 100)}.jpg`,
        }));
        setMentors(mappedMentors);
      }
    } catch (e) {
      console.error("Erreur récupération des mentors", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMentors();
  }, []);

  const handleRequestMentoring = async (mentor: Mentor) => {
    addMentor(mentor);
    const today = new Date();
    const start = today.toISOString().split("T")[0];
    const end = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

    await submitMentoringRequest(
      mentor.name.split(" ").slice(-1)[0],
      start,
      end,
    );
  };

  const filteredMentors = mentors.filter((mentor) => {
    const query = searchQuery.toLowerCase();
    return (
      mentor.name.toLowerCase().includes(query) ||
      mentor.email.toLowerCase().includes(query) ||
      mentor.profession.some((exp) => exp.toLowerCase().includes(query))
    );
  });

  return (
    <div className="container mx-auto p-4">
      {/* Search + Add button row */}
      <div className="flex justify-between items-center mb-6">
        <div className="relative w-full max-w-xs">
          <span className="absolute inset-y-0 left-3 flex items-center text-[#3D4C5E]">
            <SearchIcon size={20} />
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un mentor"
            className="pl-10 w-full h-12 bg-[#F6F8F9] rounded-xl text-sm text-[#3D4C5E] border-none focus:ring-2 focus:ring-[#0286BD]"
          />
        </div>
        <Button
          className="ml-4 bg-[#0286BD] h-12 text-white font-medium px-4 rounded-[18px] flex items-center gap-2 hover:bg-[#0275a8]"
          onClick={openModal}
        >
          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-300">
            <Plus size={14} className="text-white" />
          </div>
          <span>Créer un mentor</span>
        </Button>
      </div>

      {/* Mentor list */}
      <div className="bg-gray-100 shadow-md rounded-lg">
        <div className="px-4 py-2 font-medium text-gray-700 flex justify-between">
          <span>Nom</span>
        </div>

        {loading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="animate-spin text-blue-600 w-6 h-6" />
          </div>
        ) : filteredMentors.length > 0 ? (
          <ul>
            {filteredMentors.map((mentor) => (
              <li
                key={mentor.id}
                className="flex items-center justify-between p-4 border-b border-gray-200 last:border-none hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <img
                    src={mentor.image}
                    alt={mentor.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {mentor.name}
                    </div>
                    <div className="text-sm text-gray-500">{mentor.email}</div>
                  </div>
                </div>
                <Button
                  onClick={() => handleRequestMentoring(mentor)}
                  className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-4 py-2 rounded-full"
                >
                  Demander un mentorat
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-4 text-center text-gray-500">
            Aucun mentor trouvé.
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6">
            <AddMentor
              onSuccess={() => {
                closeModal();
                fetchMentors();
              }}
            />
            <div className="mt-4 text-right">
              <Button variant="outline" onClick={closeModal}>
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Requests button */}
      {/* <div className="mt-6 text-right">
        <Button variant="outline" onClick={viewReq}>
          Voir les demandes
        </Button>
      </div> */}
    </div>
  );
};

export default MentorList;
